import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

/**
 * Module-wide as-of date for Entity Management (FD-4464).
 *
 * Every economic surface in the module — structure chart, registry, D&O slate,
 * beneficial ownership register, company panel — reads this one value, so moving it
 * recomputes all of them together.
 *
 * **It is an ordinary date, not a period end.** Per BE (2026-08-17): period ends have
 * nothing to do with the ledger, `asOfDate` can be any date, and transactions are
 * timezone independent — a transaction on 2026-08-14 happened on that day everywhere.
 * So this is a plain calendar date, serialised from local components via
 * `dateToPlainDateString`, never through `toISOString()`, which would shift it a day
 * west of UTC.
 *
 * An earlier version modelled this as "the latest closed GL period-end", held it null
 * until a ledger read supplied it, and gated economics on having one. That concept
 * does not exist; the default is simply today.
 */
export function getDefaultAsOfDate(): Date {
  return new Date();
}

/** Holds an explicit user selection; null means "use today". */
const asOfDateSelectionAtom = atom<Date | null>(null);

export const entityManagementAsOfDateAtom = atom(
  // Resolved on read rather than at module load, so a long-lived session does not keep
  // reporting the day it started on.
  (get) => get(asOfDateSelectionAtom) ?? getDefaultAsOfDate(),
  (_get, set, value: Date | null) => set(asOfDateSelectionAtom, value)
);

/**
 * Read/write access to the module date context. Surfaces render their own as-of chip
 * from this value — the shell owns the context, not the chip.
 */
export function useEntityManagementAsOfDate() {
  return useAtom(entityManagementAsOfDateAtom);
}

/**
 * Header actions for the active surface.
 *
 * The actions render in the app's own header row, alongside the breadcrumb, so the
 * module matches the rest of the app rather than stacking a second header. Surfaces
 * register their handlers here; an action with no handler is not rendered.
 */
export interface IEntityManagementHeaderActions {
  onAddEntity?: () => void;
  /**
   * Adds a relationship between two existing entities. Registered alongside
   * `onAddEntity` — the prototype's header carries both, and a structure with entities
   * but no edges is the state this action exists to get out of.
   */
  onAddHolding?: () => void;
  /**
   * Receives the header button as an anchor, because the chart's Export is a menu —
   * PNG viewport / PNG full graph / PDF (FD-4470) — and the button it hangs off lives
   * here in the shell rather than on the surface.
   *
   * The surface owns the menu. Handing it an anchor keeps the shell ignorant of export
   * formats, where a declarative option list would move menu semantics up here for one
   * surface's benefit — the registry's Export CSV needs no menu at all.
   */
  onExport?: (anchorEl: HTMLElement) => void;
  onOpenGuide?: () => void;
}

export const entityManagementHeaderActionsAtom = atom<IEntityManagementHeaderActions>({});

/**
 * Registers header actions, and removes them again on unmount.
 *
 * The removal matters: the atom is module-global, so without it navigating Registry →
 * D&O Slate would leave the Registry's handlers in the header, still closing over an
 * unmounted surface.
 *
 * **Merges rather than replaces, and cleans up only its own keys.** More than one
 * component contributes: the shell owns Guide, while `Export` belongs to the chart
 * canvas, which is the only place that holds the laid-out graph, the active lens and the
 * routing mode. React runs child effects before parent ones, so a hook that replaced the
 * whole atom would let the shell's registration wipe the canvas's `onExport` on every
 * mount. Merging makes registration order irrelevant.
 */
export function useRegisterHeaderActions(actions: IEntityManagementHeaderActions): void {
  const setActions = useSetAtom(entityManagementHeaderActionsAtom);
  const { onAddEntity, onAddHolding, onExport, onOpenGuide } = actions;

  useEffect(() => {
    const mine: IEntityManagementHeaderActions = {};
    if (onAddEntity) {
      mine.onAddEntity = onAddEntity;
    }
    if (onAddHolding) {
      mine.onAddHolding = onAddHolding;
    }
    if (onExport) {
      mine.onExport = onExport;
    }
    if (onOpenGuide) {
      mine.onOpenGuide = onOpenGuide;
    }

    setActions((previous) => ({ ...previous, ...mine }));

    return () =>
      setActions((previous) => {
        const next = { ...previous };
        // Only the handlers this call contributed, and only if they are still ours —
        // a remount elsewhere may already have replaced them.
        (Object.keys(mine) as (keyof IEntityManagementHeaderActions)[]).forEach((key) => {
          if (next[key] === mine[key]) {
            delete next[key];
          }
        });

        return next;
      });
  }, [setActions, onAddEntity, onAddHolding, onExport, onOpenGuide]);
}

/** Retained for surfaces that only read the value. */
export function useEntityManagementAsOfDateValue() {
  return useAtomValue(entityManagementAsOfDateAtom);
}
